import { createApp, App, Component, h, reactive } from 'vue';
import { createPinia, Pinia } from 'pinia';

export type VueBridge = ReturnType<typeof createVueBridge>;

export interface VueBridgeOptions {
}

export function createVueBridge() {
  const mountedComponents: Map<HTMLElement, any> = new Map();
  const pinia: Pinia = createPinia();

  //#region Create & Init
  // Пустое приложение-контейнер
  const app: App = createApp({
    name: 'VueBridgeRoot',
    render: () => null
  });

  app.use(pinia);
  //#endregion

  function mount(
    container: HTMLElement,
    component: Component,
    props: Record<string, any> = {},
    slots: Record<string, () => any> = {},
    events: Record<string, Function> = {}
  ): { unmount: () => void; updateProps: (newProps: Record<string, any>) => void } {
    // Реактивные props для обновления
    const reactiveProps = reactive({ ...props });

    // Создаём wrapper компонент
    const WrapperComponent = {
      name: 'VueBridgeWrapper',
      setup() {
        return () => h(component, {
          ...reactiveProps,
          // Преобразуем events в onXxx формат
          ...Object.entries(events).reduce((acc, [event, handler]) => {
            const eventName = 'on' + event.charAt(0).toUpperCase() + event.slice(1);
            acc[eventName] = handler;
            return acc;
          }, {} as Record<string, any>)
        }, slots);
      }
    };

    // Создаём отдельный экземпляр приложения, но с общим контекстом
    const instance = createApp(WrapperComponent);

    // Используем тот же Pinia store
    // if (pinia) {
    //   instance.use(pinia);
    // }

    // Копируем глобальные компоненты и директивы
    instance._context.components = app._context.components;
    instance._context.directives = app._context.directives;
    instance._context.provides = app._context.provides;

    // Монтируем
    const vm = instance.mount(container);

    // Сохраняем для cleanup
    mountedComponents.set(container, { instance, vm, reactiveProps });

    return {
      unmount: () => unmount(container),
      updateProps: (newProps: Record<string, any>) => {
        Object.assign(reactiveProps, newProps);
      }
    };
  }

  /**
   * Размонтирование компонента
   */
  function unmount(container: HTMLElement): void {
    const mounted = mountedComponents.get(container);
    if (mounted) {
      mounted.instance.unmount();
      mountedComponents.delete(container);
    }
  }

  /**
   * Размонтировать все компоненты
   */
  function destroyComponents(): void {
    mountedComponents.forEach((_, container) => {
      unmount(container);
    });

    mountedComponents.clear();
  }

  return {
    app,
    pinia,
    mount,
    unmount,
    mountedComponents,
    destroyComponents,
  };
}
